import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@shared/hooks/useAuth';
import { LoadingOverlay, AppHeader, AppLauncher, Footer, StagingBanner } from '@shared/ui-react';
import type { NavItem } from '@shared/ui-react';
import { routes } from './types/routes';

// Code splitting: Lazy-load pages for better performance
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const FeaturedShowcasePage = lazy(() => import('./pages/FeaturedShowcasePage'));
const ShowcaseManagerPage = lazy(() => import('./pages/ShowcaseManagerPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

function App() {
  const { profile, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Navigation structure for AppHeader
  const navItems: NavItem[] = [
    { label: 'Dashboard', to: routes.dashboard },
    { label: 'Showcase Manager', to: routes.showcases },
    { label: 'Featured Showcase', to: routes.featured },
    { label: 'Analytics', to: routes.analytics },
    { label: 'Settings', to: routes.settings },
  ];

  return (
    <>
      <StagingBanner />
      <LoadingOverlay loading={loading}>
        <div className="app-layout">
          <AppLauncher
          isAuthenticated={!!profile}
          currentApp="assetbee"
          position="left"
          enableParallax={true}
        />
        <AppHeader
          appName="AssetBee Admin"
          logoUrl="https://intranet.weretrade.com/logos/assetBee.svg"
          variant="glass"
          sticky
          showThemeToggle
          showUserWidget
          navItems={navItems}
          activeItem={location.pathname}
          onNavClick={(to) => navigate(to)}
        />
        <main className="app-main">
          <Suspense fallback={<div className="loading">Loading page...</div>}>
            <Routes>
              <Route path={routes.home} element={<Navigate to={routes.dashboard} replace />} />
              <Route path={routes.dashboard} element={<DashboardPage />} />
              <Route path={routes.featured} element={<FeaturedShowcasePage />} />
              <Route path={routes.showcases} element={<ShowcaseManagerPage />} />
              <Route path={routes.analytics} element={<AnalyticsPage />} />
              <Route path={routes.settings} element={<SettingsPage />} />
              <Route path="*" element={<Navigate to={routes.dashboard} replace />} />
            </Routes>
          </Suspense>
        </main>
          <Footer />
        </div>
      </LoadingOverlay>
    </>
  );
}

export default App;
